import { AxiosInstance } from "axios"
import { DateType } from "../../../../shared/components/filters/selectDateTypeFilter/SelectDateTypeFilter"
import { AmountUnit, MonetaryAmount } from "../../../currency/currencyTypes"
import { MeetingServiceOrder, SalesByAccount } from "./accountingTypes"
import { ReportDetails, ReportSummary } from "../../controlKitchenApi"
import { PaymentMethodType } from "../../../payments/paymentTypes"
import { Order } from "../../controlKitchenTypes"
import { OrderFilterType } from "../../../../shared/components/filters/orderTypeFilter/helpers"

export enum AccountingQuery {
    USER_BALANCE_LIST = "user-balance-by-period",
    ORDERS_TRANSACTIONS_LIST = "accounting-orders-transactions-by-period",
    SUBSCRIPTIONS_LIST = "subscriptions-for-merchant",
    PRODUCT_SALES = "get-all-product-sales",
    BALANCE = "get-balance-by-period",
    CUSTOMERS = "get-accounting-customers",
    ACCOUNTING_ORDERS = "accounting-orders-meeting-service",
}

type FiltersParams = Readonly<{
    merchantId: number
    merchantIds?: string
    orderId?: string
    startDate?: string
    endDate?: string
    customerId?: string
    userGroupId?: string
    email?: string
    paymentMethods?: string
    creditCardLast4?: string
    shopIds?: string
    dateType: DateType
    tab?: string
}>

type PaymentMethod = {
    name: string
    amount: MonetaryAmount
}

type Account = {
    name: string
    amount: MonetaryAmount
    numberOfOrders: number
    paymentMethods?: PaymentMethod[]
}

type Category = {
    name: string
    accounts: Account[]
    amount: MonetaryAmount
}

export type BalanceReport = Readonly<{
    reportDetails: ReportDetails
    categories: Category[]
}>

export type FetchBalanceResponse = BalanceReport

type FetchBalanceByPeriodParams = Readonly<FiltersParams>

export async function fetchBalanceByPeriod(
    { merchantId, ...rest }: FetchBalanceByPeriodParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchBalanceResponse> {
    const url = `/merchants/${merchantId}/accounting/balance`

    const { data } = await axios.get(url, {
        params: rest,
        signal,
    })

    return data
}

//Subscription ::::::::::::::::::::::::::::::::::::::

type SubscriptionDetails = {
    validFrom: string
    validTo?: string
    amount: {
        amount: number
        scale: number
        currency: string
    }
    paymentDetails: {
        method: PaymentMethodType
        subscriptionId: string
    }
    customer: {
        initials: string
        employeeId: string
        displayName: string
        givenName: string
        surName: string
        email: string
        userGroup: {
            name: string
        }
    }
    order: {
        id: number
        uid: string
        orderType: string
        accountingCustomerParty: {
            name: string
        }
        created: string
    }
    orderLines?: {
        unit: AmountUnit
        price: MonetaryAmount
    }[]
}

export type Subscription = {
    id: number
    name: string
    subscriptionDetails: SubscriptionDetails
}

type FetchSubscriptionParams = Readonly<
    {
        subscriptionIds?: string
    } & Omit<FiltersParams, "dateType">
>

export type FetchSubscriptionResponse = Readonly<{
    reportDetails: ReportDetails
    subscriptions: Subscription[]
}>

export async function fetchSubscriptionByPeriod(
    { merchantId, ...rest }: FetchSubscriptionParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchSubscriptionResponse> {
    const url = `/merchants/${merchantId}/accounting/subscriptions`

    const { data } = await axios.get(url, {
        params: rest,
        signal,
    })

    return data
}

// Product sales ::::::::::::::::::::::::::::::::::::::

type ProductSale = {
    id: number
    name: string
    code?: string
    numberOfOrders: number
    amount: MonetaryAmount
}

type ProductGroup = {
    id: string
    name: string
    code?: string
    totalAmount: MonetaryAmount
    productSales: ProductSale[]
}

export type FetchProductSalesResponse = Readonly<{
    reportDetails: ReportDetails
    reportSummary: ReportSummary
    productGroups: ProductGroup[]
}>

type FetchProductSalesByPeriodParams = Readonly<
    {
        orderType: OrderFilterType
    } & FiltersParams
>

export async function fetchProductSalesByPeriod(
    { merchantId, ...rest }: FetchProductSalesByPeriodParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchProductSalesResponse> {
    const url = `/merchants/${merchantId}/accounting/productSale`

    const { data } = await axios.get(url, {
        params: rest,
        signal,
    })

    return data
}

//Customer ::::::::::::::::::::::::::::::::::::::

type CustomerCategory = {
    name: string
    accounts: Account[]
}

type CustomerSale = {
    id: number
    displayName: string
    categories: CustomerCategory[]
    amount: MonetaryAmount
}

export type FetchCustomerSalesResponse = Readonly<{
    reportDetails: ReportDetails
    customerSales?: CustomerSale[]
}>

type FetchCustomerSalesParams = Readonly<{
    merchantId: number
    shopId: string | undefined
    startDate: string
    endDate: string
    customerId?: string | undefined
    customerGroupId?: string | undefined
}>

export async function fetchCustomerSalesByPeriod(
    { shopId, startDate, endDate, merchantId, customerId, customerGroupId }: FetchCustomerSalesParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchCustomerSalesResponse> {
    const url = `/merchants/${merchantId}/accounting/customerSale`

    const { data } = await axios.get(url, {
        params: {
            startDate,
            endDate,
            shopId,
            customerId,
            customerGroupId,
        },
        signal,
    })

    return data
}

// New customer endpoint

type GetAccountingCustomerParams = Readonly<
    {
        merchantId: number
        hasSales?: boolean
        orderType: OrderFilterType
    } & FiltersParams
>

export type AccountingCustomer = Readonly<{
    id: number
    name: string
    isActive: boolean
    isOfficeHotel: boolean
    hasSales?: boolean
    userGroups?: {
        id: number
        name: string
        isActive: boolean
        hasSales: boolean
    }[]
}>

export type GetCustomersResponse = Readonly<{
    customers: AccountingCustomer[]
}>

// Dette kald henter listen af customers(companies) for en merchant, inklusive customer groups(tenants, user groups).Herudover er der for hver kunde og lejer et hasSales flag, som skal bruges til at implementere en filter toggle, så man kan nøjes med at se kunder med salg.
export async function getAccountingCustomers(
    { merchantId, ...rest }: GetAccountingCustomerParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<GetCustomersResponse> {
    const url = `/merchants/${merchantId}/accounting/customers`

    const { data } = await axios.get(url, {
        params: rest,
        signal,
    })

    return data
}

type GetCustomerSalesByAccountParams = Readonly<{
    merchantId: number
    customerId: number
    shopId?: string | undefined
    startDate: string
    endDate: string
}>

type AccountingReportDetails = {
    startDate: string
    endDate: string
    merchantId: number
    customerId: string
    reportTime: string
    customerGroupId?: string
}

type ReportData = {
    reportDetails: AccountingReportDetails
    salesByAccount: SalesByAccount
}

export type CustomerSalesByAccountResponse = ReportData

// Dette kald henter salg for en bestemt kunde(ikke lejer) fordelt på accounts og products givet et merchantId og customerId.Denne skal du kalde, hvis bruger udvider en kunde, som ikke er en lejer.
export async function getCustomerSalesByAccounts(
    { merchantId, customerId, shopId, startDate, endDate }: GetCustomerSalesByAccountParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<CustomerSalesByAccountResponse> {
    const url = `/merchants/${merchantId}/accounting/customers/${customerId}/salesByAccount`

    const { data } = await axios.get(url, {
        params: {
            startDate,
            endDate,
            shopId,
        },
        signal,
    })

    return data
}

///

type GetCustomerGroupSalesByAccountParams = Readonly<{
    merchantId: number
    customerId: number
    customerGroupId: number
    shopId?: string
    startDate: string
    endDate: string
}>

export type CustomerGroupSalesByAccountResponse = ReportData

// Dette kald henter salg for en bestemt kundegruppe(lejer) fordelt på accounts og products givet et merchantId, customerId og customerGroupId.Denne skal du kalde, hvis brugeren udvider en kunde, som er en lejer.
export async function getCustomerGroupSalesByAccounts(
    { merchantId, customerId, customerGroupId, shopId, startDate, endDate }: GetCustomerGroupSalesByAccountParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<CustomerGroupSalesByAccountResponse> {
    const url = `/merchants/${merchantId}/accounting/customers/${customerId}/customerGroups/${customerGroupId}/salesByAccount`

    const { data } = await axios.get(url, {
        params: {
            startDate,
            endDate,
            shopId,
        },
        signal,
    })

    return data
}

// Cancel subscription

type CancelSubscriptionParams = Readonly<{
    orderId: number | string | undefined
    ignoreTerms: boolean
}>

type CancelSubscriptionResponse = Readonly<{
    transactionResponse: {
        subject: string
    }
}>

export async function cancelSubscription(
    { orderId, ignoreTerms }: CancelSubscriptionParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<CancelSubscriptionResponse> {
    const url = `/orders/subscriptions/${orderId}/unsubscribe`

    const { data } = await axios.post(
        url,
        {
            ignoreTerms,
        },
        {
            signal,
        },
    )

    return data
}

type FetchCustomerGroupPayrollFilesZipParams = Readonly<{
    merchantId: number
}>

export type FetchCustomerGroupPayrollFilesZipResponse = Readonly<{
    orders: Order[] | null
}>

export async function fetchCustomerGroupPayrollFilesZip(
    { merchantId }: FetchCustomerGroupPayrollFilesZipParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchCustomerGroupPayrollFilesZipResponse> {
    const url = `/merchants/${merchantId}/accounting/payrollFiles`

    const { data } = await axios.get(url, {
        signal,
    })

    return data
}

// accounting orders

export type FetchAccountingOrdersResponse = Readonly<{
    orders: Order[] | null
}>

export async function fetchAccountingOrdersByPeriod(
    { merchantId, ...rest }: FiltersParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchAccountingOrdersResponse> {
    const url = `/merchants/${merchantId}/accounting/orders`

    const { data } = await axios.get(url, {
        params: rest,
        signal,
    })

    return data
}

// Customer balance

type User = {
    uid: string
    employeeId: string
    displayName?: string
    name: string
    initials: string
    email: string
}

export type UserBalance = {
    user: User
    totalAmount: MonetaryAmount
}

type CustomerBalanceReport = Readonly<{
    reportDetails: ReportDetails
    reportSummary: ReportSummary
    usersBalance: UserBalance[]
}>

type FetchCustomerBalanceReportParams = Readonly<{
    merchantId: number
    startDate?: string
    endDate?: string
    shopId?: string
    customerId?: string
    customerGroupId?: string
}>

export type FetchCustomerBalanceReportResponse = CustomerBalanceReport

export async function fetchCustomerBalanceReportByPeriod(
    { shopId, startDate, endDate, merchantId, customerId, customerGroupId }: FetchCustomerBalanceReportParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchCustomerBalanceReportResponse> {
    const url = `/merchants/${merchantId}/accounting/userBalance`

    const { data } = await axios.get(url, {
        params: {
            startDate,
            endDate,
            shopId,
            customerId,
            customerGroupId,
        },
        signal,
    })

    return data
}

// Meeting Service
type FetchAccountingMeetingServiceParams = Readonly<{
    merchantId: number
    orderId?: string
    pendingApproval?: boolean
    invoiceStatus?: string
    isBooked?: string
    startBookedDate?: string
    endBookedDate?: string
    startDeliveryDate?: string
    endDeliveryDate?: string
    customerId?: string
    userGroupId?: string
    email?: string
    paymentMethod?: string
    creditCardLast4?: string
    merchantIds?: string
    shopIds?: string
    satelliteIds?: string
    buyerPartyIds?: string
}>

export type FetchAccountingMeetingServiceResponse = Readonly<{
    orders: MeetingServiceOrder[]
}>

export async function fetchAccountingMeetingService(
    { merchantId, ...rest }: FetchAccountingMeetingServiceParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchAccountingMeetingServiceResponse> {
    const url = `/merchants/${merchantId}/accounting/orders/meetingService`

    const { data } = await axios.get(url, {
        params: rest,
        signal,
    })

    return data
}
